import { useEffect, useRef, useState } from "react";
import { ReauthDialog } from "@/components/settings/ReauthDialog";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import { setStepUpHandler, type ApiClientError, type StepUpKind } from "@/lib/api";
import { useAuth } from "@/lib/auth/AuthContext";
import {
  clearStepUpAttempt,
  isMfaRequired,
  noteStepUpAttempt,
  providerDidNotAskMessage,
  recentStepUpAttempt,
  stepUpRefusedMessage,
} from "@/lib/auth/mfaStepUp";

/**
 * #1110: the re-authentication dialog of every privileged write that has no dialog of its own
 * (alert routing, the Teams webhook, an AI app's write approve and the AI app switches, disable,
 * enable and mark storage removed, connector access, the audit exports). A request sent with
 * `stepUp` (`lib/api.ts`) that the server refuses with `REAUTH_REQUIRED` or `MFA_REQUIRED` waits
 * here; "Sign in again" confirms identity and the SAME request is sent again, so the screen that
 * asked sees one save that took a little longer. Cancel fails it with "Sign in again to save this
 * change.". The server rule is the destination Save's (`requirePrivilegedFromSettings`).
 *
 * When the provider leaves the page to confirm (MSAL's redirect), the held request is not carried
 * across it: these are one-click changes, and the person makes them again on return, as on the
 * Directory groups tab.
 */

const COPY: Record<
  StepUpKind,
  { title: string; description: string; heldTitle: string; held: string; footnote: string }
> = {
  change: {
    title: "Sign in again to save this change",
    description:
      "Your session is still valid, but you last signed in a while ago. This change needs a fresh sign-in.",
    heldTitle: "Nothing has been saved yet",
    held: "Your change is held. Signing in again may take you to your sign-in page. If it does, make the change again when you are back.",
    footnote: "Cancelling saves nothing and leaves the screen as it is.",
  },
  export: {
    title: "Sign in again to download this export",
    description:
      "Your session is still valid, but you last signed in a while ago. An audit export is a bulk copy of who did what, so it needs a fresh sign-in.",
    heldTitle: "Nothing has been downloaded yet",
    held: "Signing in again may take you to your sign-in page. If it does, start the export again when you are back.",
    footnote: "Cancelling downloads nothing.",
  },
};

/** #1110 review: the sentence when a sign-in again did not refresh the sign-in time. */
export const SIGN_IN_NOT_REFRESHED =
  "Your sign-in was not refreshed. Try again, or cancel, sign out and sign in again.";

interface Pending {
  kind: StepUpKind;
  code: "REAUTH_REQUIRED" | "MFA_REQUIRED";
  blocked: boolean;
  resolvers: Array<(go: boolean) => void>;
}

export function StepUpHost() {
  const { reauthenticate, account } = useAuth();
  const { me } = useTenantInstance();
  const accountEmail = account?.email ?? null;
  const [pending, setPending] = useState<Pending | null>(null);
  const [isRetrying, setRetrying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pendingRef = useRef<Pending | null>(null);
  const mfaRef = useRef(me?.mfa);
  const emailRef = useRef(accountEmail);
  useEffect(() => {
    mfaRef.current = me?.mfa;
    emailRef.current = accountEmail;
  }, [me?.mfa, accountEmail]);

  useEffect(() => {
    const show = (next: Pending | null) => {
      pendingRef.current = next;
      setPending(next);
    };
    setStepUpHandler({
      confirm(refusal: ApiClientError, kind: StepUpKind, again: boolean) {
        return new Promise<boolean>((resolve) => {
          const mfa = isMfaRequired(refusal);
          // The provider just came back without a second factor: say why, never redirect again.
          const blocked = mfa && recentStepUpAttempt(emailRef.current);
          const open = pendingRef.current;
          setRetrying(false);
          // #1110 review: the same request refused again after a confirmed sign-in (a provider
          // that handed back a token with the old sign-in time) says so, never the same prompt.
          setError(
            blocked
              ? providerDidNotAskMessage(mfaRef.current)
              : again
                ? SIGN_IN_NOT_REFRESHED
                : null,
          );
          show({
            kind: open?.kind ?? kind,
            code: mfa ? "MFA_REQUIRED" : "REAUTH_REQUIRED",
            blocked,
            resolvers: [...(open?.resolvers ?? []), resolve],
          });
        });
      },
      succeeded() {
        clearStepUpAttempt();
        if (!pendingRef.current) setRetrying(false);
      },
    });
    return () => setStepUpHandler(null);
  }, []);

  const settle = (go: boolean) => {
    const open = pendingRef.current;
    pendingRef.current = null;
    setPending(null);
    open?.resolvers.forEach((resolve) => resolve(go));
  };

  const confirm = () => {
    const open = pendingRef.current;
    if (!open || open.blocked) return settle(false);
    if (open.code === "MFA_REQUIRED") noteStepUpAttempt(emailRef.current);
    setRetrying(true);
    setError(null);
    void reauthenticate().then(
      (outcome) => {
        // The provider is leaving the page: the request goes with it.
        if (outcome === "redirecting") return;
        if (outcome === "failed") {
          setRetrying(false);
          setError("Your identity was not confirmed. Try again, or cancel and come back later.");
          return;
        }
        setRetrying(false);
        settle(true);
      },
      (cause: unknown) => {
        // The step could not start: say why in plain words and keep the request held (#2006).
        // Nobody was sent to the provider: the "just asked" marker must not block the next try.
        clearStepUpAttempt();
        setRetrying(false);
        setError(stepUpRefusedMessage(cause));
      },
    );
  };

  const copy = COPY[pending?.kind ?? "change"];
  return (
    <ReauthDialog
      open={pending !== null}
      isRetrying={isRetrying}
      error={error}
      onConfirm={confirm}
      onCancel={() => {
        setError(null);
        setRetrying(false);
        settle(false);
      }}
      title={copy.title}
      description={copy.description}
      heldTitle={copy.heldTitle}
      heldMessage={copy.held}
      footnote={copy.footnote}
      cancelLabel="Cancel"
      code={pending?.code ?? "REAUTH_REQUIRED"}
      blocked={pending?.blocked ?? false}
      remember={{ days: me?.mfa?.rememberDays ?? 0, account: accountEmail }}
    />
  );
}
